import HBLoading from "@/components/HBLoading";
import Image from "@/components/Image";
import usePngBg from "@/utils/hooks/usePngBg";
import imageUrl from "@/utils/imageUrl";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./index.module.less";
import { type MagnifierPhase } from "./useMagnifierState";

interface IMagnifierPreviewProps {
  pin: IPin;
  cardRef: React.RefObject<HTMLDivElement>; // 卡片的 ref，用于获取位置
  phase: MagnifierPhase; // 动画阶段
  onAnimationEnd: () => void; // 动画结束回调
  onMouseEnter?: () => void; // 鼠标进入放大框
  onMouseLeave?: () => void; // 鼠标离开放大框
}

// 放大倍数常量
const SCALE_RATIO_NORMAL = 1.6; // 普通卡片放大倍数
const SCALE_RATIO_LONG = 1.6; // 长图卡片放大倍数
const LONG_IMAGE_RATIO = 1.5; // 判断长图的高宽比阈值（高度/宽度 > 1.5 视为长图）

/**
 * 放大镜浮窗组件
 * 从卡片位置放大显示高清大图
 */
export default function MagnifierPreview({
  pin,
  cardRef,
  phase,
  onAnimationEnd,
  onMouseEnter,
  onMouseLeave,
}: IMagnifierPreviewProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const isGif = pin?.file?.type === "image/gif";
  const [cardRect, setCardRect] = useState<DOMRect | null>(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });

  // 使用与卡片相同的背景逻辑
  const imageBgClass = usePngBg(pin?.file?.theme || "");

  // 获取卡片位置
  useEffect(() => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCardRect(rect);
    }
  }, [cardRef]);

  // 计算放大倍数：根据卡片宽高比动态调整
  const scaleRatio = useMemo(() => {
    if (!cardRect) return SCALE_RATIO_NORMAL;

    const aspectRatio = cardRect.height / cardRect.width;
    // 如果高度/宽度 > 1.5，视为长图，使用较小的放大倍数
    return aspectRatio > LONG_IMAGE_RATIO
      ? SCALE_RATIO_LONG
      : SCALE_RATIO_NORMAL;
  }, [cardRect]);

  // 获取图片真实宽高比
  const imageAspectRatio = useMemo(() => {
    if (!pin?.file?.width || !pin?.file?.height) {
      return cardRect ? cardRect.height / cardRect.width : 1;
    }
    return pin.file.height / pin.file.width;
  }, [pin?.file?.width, pin?.file?.height, cardRect]);

  // 处理鼠标移动，实现裸眼3D效果
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!previewRef.current) return;

    const rect = previewRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // 计算鼠标相对于中心的偏移
    const offsetX = e.clientX - centerX;
    const offsetY = e.clientY - centerY;

    // 计算旋转角度（最大±10度）
    const maxRotation = 10;
    const rotateY = (offsetX / (rect.width / 2)) * maxRotation;
    const rotateX = -(offsetY / (rect.height / 2)) * maxRotation;

    setTransform({ rotateX, rotateY });
  };

  // 鼠标离开时重置旋转
  const handleMouseLeaveInternal = (e: React.MouseEvent<HTMLDivElement>) => {
    setTransform({ rotateX: 0, rotateY: 0 });
    onMouseLeave?.();
  };

  const handleMouseEnterInternal = (e: React.MouseEvent<HTMLDivElement>) => {
    onMouseEnter?.();
  };

  // 处理动画结束事件
  const handleAnimationEndInternal = (
    e: React.AnimationEvent<HTMLDivElement>
  ) => {
    // 只处理放大镜自身的动画，忽略子元素的动画事件
    if (e.target === previewRef.current) {
      onAnimationEnd();
    }
  };

  // 计算浮窗位置和动画起始位置
  const { previewStyle, animationStyle } = useMemo(() => {
    if (!cardRect) {
      return { previewStyle: {}, animationStyle: {} };
    }

    // 基于卡片宽度放大，高度根据图片真实宽高比计算
    let previewWidth = cardRect.width * scaleRatio;
    let previewHeight = previewWidth * imageAspectRatio; // 使用图片真实宽高比

    // 屏幕可用空间（留出 margin）
    const margin = 40; // 增加边距，确保有足够空间
    const maxWidth = window.innerWidth - margin * 2;
    const maxHeight = window.innerHeight - margin * 2;

    // 如果放大后超出屏幕，按比例缩小
    if (previewWidth > maxWidth || previewHeight > maxHeight) {
      const widthScale = maxWidth / previewWidth;
      const heightScale = maxHeight / previewHeight;
      const finalScale = Math.min(widthScale, heightScale);

      previewWidth = previewWidth * finalScale;
      previewHeight = previewHeight * finalScale;
    }

    // 计算卡片中心点
    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    // 放大框中心与卡片中心对齐
    let finalLeft = cardCenterX - previewWidth / 2;
    let finalTop = cardCenterY - previewHeight / 2;

    // 确保不超出屏幕边界
    // 水平方向边界检测
    if (finalLeft < margin) {
      finalLeft = margin;
    } else if (finalLeft + previewWidth > window.innerWidth - margin) {
      finalLeft = window.innerWidth - previewWidth - margin;
    }

    // 垂直方向边界检测
    if (finalTop < margin) {
      finalTop = margin;
    } else if (finalTop + previewHeight > window.innerHeight - margin) {
      finalTop = window.innerHeight - previewHeight - margin;
    }

    return {
      previewStyle: {
        left: `${finalLeft}px`,
        top: `${finalTop}px`,
        width: `${previewWidth}px`,
        height: `${previewHeight}px`,
      },
      // CSS 变量用于动画起始位置
      animationStyle: {
        "--card-left": `${cardRect.left}px`,
        "--card-top": `${cardRect.top}px`,
        "--card-width": `${cardRect.width}px`,
        "--card-height": `${cardRect.height}px`,
        "--preview-left": `${finalLeft}px`,
        "--preview-top": `${finalTop}px`,
        "--preview-width": `${previewWidth}px`,
        "--preview-height": `${previewHeight}px`,
      } as React.CSSProperties,
    };
  }, [cardRect, scaleRatio, imageAspectRatio]);

  // 使用与卡片相同的图片尺寸，复用浏览器缓存，避免重复加载
  const previewImageUrl = useMemo(() => {
    return imageUrl(pin.file, {
      width: 240,
    });
  }, [pin.file]);

  // 2倍图也使用与卡片相同的尺寸
  const previewImageUrl2x = useMemo(() => {
    return imageUrl(pin.file, {
      width: 480,
    });
  }, [pin.file]);

  // 处理图片加载完成
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // 对于 GIF，需要特殊处理，因为 GIF 可能不会触发 onLoad 事件
  useEffect(() => {
    if (!isGif) return;

    let cleanup: (() => void) | undefined;

    // 延迟一下，确保 DOM 已经渲染
    const timer = setTimeout(() => {
      // 查找 img 元素
      const img = previewRef.current?.querySelector(
        "img.hb-image"
      ) as HTMLImageElement;
      if (img) {
        // 如果图片已经加载完成（可能是缓存的）
        if (img.complete && img.naturalWidth > 0) {
          setImageLoaded(true);
          return;
        }

        // 监听 load 事件
        const handleLoad = () => setImageLoaded(true);
        const handleError = () => setImageLoaded(true);
        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);

        // 保存清理函数
        cleanup = () => {
          img.removeEventListener("load", handleLoad);
          img.removeEventListener("error", handleError);
        };
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      cleanup?.();
    };
  }, [isGif, previewImageUrl]);

  // 根据 phase 确定动画类名
  const phaseClassName = useMemo(() => {
    switch (phase) {
      case "entering":
        return styles.entering;
      case "exiting":
        return styles.exiting;
      case "visible":
        return styles.visible;
      default:
        return "";
    }
  }, [phase]);

  // 使用 createPortal 渲染到 body，避免被瀑布流卡片遮挡
  return createPortal(
    <div
      className={classNames(styles.preview, phaseClassName)}
      ref={previewRef}
      style={{
        ...previewStyle,
        ...animationStyle,
        transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
        transition: phase === "visible" ? "transform 0.1s ease-out" : undefined,
      }}
      data-gd-expose="dialog_expose"
      data-dialog-title="大图预览弹窗"
      data-trigger-type="快捷键"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnterInternal}
      onMouseLeave={handleMouseLeaveInternal}
      onAnimationEnd={handleAnimationEndInternal}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className={styles.imageContainer}>
        {!imageLoaded && (
          <div className={styles.loading}>
            <HBLoading size="40px" stroke="#FF5967" />
          </div>
        )}
        <Image
          className={imageBgClass}
          alt={pin.raw_text || "图片预览"}
          src={previewImageUrl}
          srcSet={`${previewImageUrl2x} 2x`}
          onLoad={handleImageLoad}
          showError={false}
          crossOrigin="anonymous"
        />
      </div>
    </div>,
    document.body
  );
}
